import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { useSearch, getTypeLabel, SearchResult } from "@/hooks/useSearch";
import PageMeta from "@/components/PageMeta";

const SearchResultCard = ({ result }: { result: SearchResult }) => {
  const isHashLink = result.url.includes("#");
  
  const content = (
    <div className="group bg-cream rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="flex flex-col sm:flex-row">
        {result.image && (
          <div className="sm:w-32 md:w-40 aspect-square sm:aspect-[4/3] flex-shrink-0 overflow-hidden">
            <img 
              src={result.image} 
              alt={result.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-sans uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              {getTypeLabel(result.type)}
            </span>
          </div>
          <h3 className="font-serif text-lg text-foreground group-hover:text-primary transition-colors mb-2">
            {result.title}
          </h3>
          <p className="text-muted-foreground text-sm font-sans line-clamp-2">
            {result.description}
          </p>
        </div>
      </div>
    </div>
  );

  if (isHashLink) {
    return (
      <a href={result.url}>
        {content}
      </a>
    );
  }

  return (
    <Link to={result.url}>
      {content}
    </Link>
  );
};

const Sok = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const results = useSearch(query);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Update URL when query changes (debounced)
    const timeout = setTimeout(() => {
      if (query.trim()) {
        setSearchParams({ q: query.trim() });
      } else {
        setSearchParams({});
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, setSearchParams]);

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const typeOrder: SearchResult["type"][] = ["bunad", "tilbehor", "blogg", "faq", "side"];

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Søk – Finn bunader, tilbehør og mer"
        description="Søk i Bunadstuggu sitt utvalg av bunader, tilbehør og informasjon. Finn det du leter etter raskt og enkelt."
        path="/sok"
      />
      <Header />
      
      <main className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-sans text-sm mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbake til forsiden
          </Link>

          {/* Search Header */}
          <div className="mb-10">
            <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
              Søk
            </p>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
              Søkeresultater
            </h1>
            
            {/* Search Input */}
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Søk etter bunader, tilbehør, spørsmål..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 py-6 text-base bg-cream border-0 focus-visible:ring-primary"
                autoFocus
              />
            </div>
          </div>

          {/* Results */}
          {query.trim() ? (
            results.length > 0 ? (
              <div className="space-y-10">
                <p className="text-muted-foreground font-sans">
                  {results.length} treff for "{query}"
                </p>
                
                {typeOrder.map((type) => {
                  const typeResults = groupedResults[type];
                  if (!typeResults || typeResults.length === 0) return null;
                  
                  return (
                    <section key={type}>
                      <h2 className="font-serif text-xl text-foreground mb-4 flex items-center gap-2">
                        {getTypeLabel(type)}
                        <span className="text-muted-foreground text-sm font-sans">
                          ({typeResults.length})
                        </span>
                      </h2>
                      <div className="space-y-4">
                        {typeResults.map((result) => (
                          <SearchResultCard key={result.id} result={result} />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-cream rounded-full mb-6">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <h2 className="font-serif text-2xl text-foreground mb-3">
                  Ingen treff
                </h2>
                <p className="text-muted-foreground font-sans mb-8 max-w-md mx-auto">
                  Vi fant ingen resultater for "{query}". Prøv et annet søkeord eller utforsk våre anbefalte sider.
                </p>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <Link
                    to="/drakt/grafferdrakt"
                    className="px-4 py-2 bg-cream text-foreground rounded-full text-sm font-sans hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    Grafferdrakt
                  </Link>
                  <Link
                    to="/drakt/lundebybunad"
                    className="px-4 py-2 bg-cream text-foreground rounded-full text-sm font-sans hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    Lundebybunad
                  </Link>
                  <Link
                    to="/tilbehor"
                    className="px-4 py-2 bg-cream text-foreground rounded-full text-sm font-sans hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    Tilbehør
                  </Link>
                  <Link
                    to="/sporsmal"
                    className="px-4 py-2 bg-cream text-foreground rounded-full text-sm font-sans hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    Spørsmål
                  </Link>
                </div>
              </div>
            )
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground font-sans">
                Skriv inn et søkeord for å starte søket
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Sok;
